'use client';
import { useState, useMemo } from 'react';

// ─── Signal Legend (shared between charts) ───────────────────────────────────

export function SignalLegend({ enteredAlphaSymbols, exitedAlphaSymbols, stocks }: {
  enteredAlphaSymbols: Set<string>;
  exitedAlphaSymbols: Set<string>;
  stocks: { symbol: string; qualityScore: number | null }[];
}) {
  const [open, setOpen] = useState(true);
  type ExpKey = 'entered-alpha' | 'exited-alpha';
  const [expanded, setExpanded] = useState<ExpKey | null>(null);

  // Inlined the previously-named `pick` helper to keep useMemo deps complete.
  // Returns stocks in `set` sorted by qualityScore desc.
  const enteredAlpha = useMemo(
    () => stocks.filter(s => enteredAlphaSymbols.has(s.symbol)).sort((a, b) => (b.qualityScore ?? 0) - (a.qualityScore ?? 0)),
    [stocks, enteredAlphaSymbols],
  );
  const exitedAlpha = useMemo(
    () => stocks.filter(s => exitedAlphaSymbols.has(s.symbol)).sort((a, b) => (b.qualityScore ?? 0) - (a.qualityScore ?? 0)),
    [stocks, exitedAlphaSymbols],
  );

  const rows: { key: ExpKey; label: string; color: string; data: typeof enteredAlpha }[] = [
    { key: 'entered-alpha', label: 'Entered Alpha Zone', color: '#22c55e', data: enteredAlpha },
    { key: 'exited-alpha',  label: 'Exited Alpha Zone',  color: '#dc2626', data: exitedAlpha  },
  ];

  return (
    <div className="absolute bottom-3 left-3 z-10 rounded-lg border border-[#1e293b]"
      style={{ background: 'rgba(13, 17, 23, 0.85)', backdropFilter: 'blur(8px)', maxHeight: '40vh', overflowY: 'auto' }}>
      <button
        onClick={() => { setOpen(p => !p); if (open) setExpanded(null); }}
        className="flex items-center gap-2 px-3 py-1.5 cursor-pointer w-full"
      >
        <span className="text-[10px] font-bold uppercase tracking-widest text-white">Signals</span>
        <svg
          className={`w-2.5 h-2.5 text-white transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-3 pb-2 flex flex-col gap-1">
          {rows.map(row => {
            const count = row.data.length;
            const hasData = count > 0;
            const isExp = expanded === row.key;
            return (
              <div key={row.key}>
                <button
                  onClick={() => hasData ? setExpanded(p => p === row.key ? null : row.key) : undefined}
                  className={`flex items-center gap-2 ${hasData ? 'cursor-pointer hover:bg-white/5' : 'cursor-default'} rounded px-1 py-0.5 -mx-1 w-full`}
                >
                  <svg width="18" height="18">
                    <circle cx="9" cy="9" r="5" fill="none" stroke={row.color} strokeWidth="2" opacity={hasData ? 1 : 0.35}>
                      {hasData && <animate attributeName="opacity" values="1;0.3;1" dur="1.4s" repeatCount="indefinite" />}
                    </circle>
                  </svg>
                  <span className="text-[11px] font-semibold" style={{ color: row.color, opacity: hasData ? 1 : 0.4 }}>{row.label}</span>
                  <span className="text-[10px] ml-auto tabular-nums" style={{ color: row.color, opacity: hasData ? 1 : 0.4, fontFamily: 'var(--font-mono)' }}>
                    {count}
                  </span>
                </button>
                {isExp && hasData && (
                  <div className="ml-6 flex flex-col gap-0.5 mb-1">
                    {row.data.map(s => (
                      <div key={s.symbol} className="flex items-center justify-between">
                        <span className="text-[11px] text-white font-semibold" style={{ fontFamily: 'var(--font-mono)' }}>{s.symbol}</span>
                        <span className="text-[10px] tabular-nums" style={{ color: row.color, fontFamily: 'var(--font-mono)' }}>{s.qualityScore?.toFixed(2) ?? '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

